import React, { useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import HomeIcon from '@mui/icons-material/Home';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AddBoxIcon from '@mui/icons-material/AddBox';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';

const socialLinks = [
    { href: "https://www.facebook.com/profile.php?id=100071854380014", label: "Facebook", Icon: FacebookIcon },
    { href: "https://twitter.com/wb4gbi", label: "Twitter", Icon: TwitterIcon },
    { href: "https://www.instagram.com/hamradiowb4gbi/", label: "Instagram", Icon: InstagramIcon },
];

const navLinks = [
    { to: "/", label: "Home", Icon: HomeIcon },
    { to: "/gallery", label: "Gallery", Icon: PhotoLibraryIcon },
    { to: "/documents", label: "Documents", Icon: DescriptionIcon },
    { to: "/about", label: "About", Icon: InfoIcon },
];

const NavCont = ({ setShowAuth, setCurrentUser, currentUser, handleAddDist, districts, handleLogout }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [repeaterAnchor, setRepeaterAnchor] = useState(null);
    const [userAnchor, setUserAnchor] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const isActive = (to) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));
    const isRepeaterActive = location.pathname.startsWith('/repeater');

    const goTo = (to) => {
        setDrawerOpen(false);
        setRepeaterAnchor(null);
        navigate(to);
    };

    const handleAdmin = () => {
        setCurrentUser({ ...currentUser, adminSettings: !currentUser.adminSettings });
    };

    const sortedDistricts = [...districts].sort((a, b) => (parseInt(a.district) > parseInt(b.district) ? 1 : -1));

    useEffect(() => {
        const checkLoginHash = () => {
            if (window.location.hash.includes('login')) {
                setShowAuth(true);
                navigate(window.location.pathname, { replace: true });
            }
        };
        checkLoginHash();
        window.addEventListener('hashchange', checkLoginHash);
        return () => window.removeEventListener('hashchange', checkLoginHash);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AppBar position="sticky" elevation={0}>
            <Toolbar sx={{ gap: 0.5 }}>
                <Typography variant="h6" sx={{ flexGrow: 1, display: { xs: 'block', md: 'none' } }}>
                    WB4GBI
                </Typography>

                {/* Desktop nav */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', flexGrow: 1, gap: 0.5 }}>
                    <Button
                        component={RouterLink}
                        to="/"
                        startIcon={<HomeIcon />}
                        color={isActive('/') ? 'primary' : 'inherit'}
                    >
                        Home
                    </Button>
                    <Button
                        startIcon={<SettingsInputAntennaIcon />}
                        endIcon={<ExpandMoreIcon />}
                        color={isRepeaterActive ? 'primary' : 'inherit'}
                        onClick={(e) => setRepeaterAnchor(e.currentTarget)}
                    >
                        Repeaters
                    </Button>
                    <Menu anchorEl={repeaterAnchor} open={Boolean(repeaterAnchor)} onClose={() => setRepeaterAnchor(null)}>
                        {sortedDistricts.map((d) => (
                            <MenuItem key={d._id} onClick={() => goTo(`/repeater/${d.district}`)}>
                                {d.district}
                            </MenuItem>
                        ))}
                        {(currentUser.isAuthenticated && currentUser.user.isAdmin > 9 && currentUser.adminSettings) && (
                            <MenuItem
                                onClick={() => { setRepeaterAnchor(null); handleAddDist(); }}
                                sx={{ justifyContent: 'center' }}
                            >
                                <AddBoxIcon fontSize="small" />
                            </MenuItem>
                        )}
                    </Menu>
                    {navLinks.slice(1).map(({ to, label, Icon }) => (
                        <Button
                            key={to}
                            component={RouterLink}
                            to={to}
                            startIcon={<Icon />}
                            color={isActive(to) ? 'primary' : 'inherit'}
                        >
                            {label}
                        </Button>
                    ))}
                </Box>

                {currentUser.isAuthenticated && (
                    <>
                        <Button
                            color="inherit"
                            startIcon={<AccountCircleIcon />}
                            endIcon={<ExpandMoreIcon />}
                            onClick={(e) => setUserAnchor(e.currentTarget)}
                        >
                            {currentUser.user.username}
                        </Button>
                        <Menu anchorEl={userAnchor} open={Boolean(userAnchor)} onClose={() => setUserAnchor(null)}>
                            {currentUser.user.isAdmin > 0 && [
                                currentUser.user.isAdmin > 8 && (
                                    <MenuItem key="user-admin" onClick={() => { setUserAnchor(null); goTo('/users'); }}>
                                        User Admin
                                    </MenuItem>
                                ),
                                currentUser.user.isAdmin > 8 && <Divider key="admin-divider-1" />,
                                <MenuItem key="admin-switch" disableRipple>
                                    <FormControlLabel
                                        control={<Switch checked={currentUser.adminSettings} onChange={handleAdmin} />}
                                        label="Admin"
                                    />
                                </MenuItem>,
                                <Divider key="admin-divider-2" />,
                            ]}
                            <MenuItem onClick={() => { setUserAnchor(null); handleLogout(); }}>Logout</MenuItem>
                        </Menu>
                    </>
                )}

                <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                    <IconButton color="inherit" onClick={() => setDrawerOpen(true)}>
                        <MenuIcon />
                    </IconButton>
                </Box>

                <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 1, gap: 0.5 }}>
                    {socialLinks.map(({ href, label, Icon }) => (
                        <IconButton key={href} component="a" href={href} target="_blank" rel="noreferrer" color="inherit" aria-label={label} size="small">
                            <Icon fontSize="small" />
                        </IconButton>
                    ))}
                </Box>
            </Toolbar>

            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: 260 }} role="presentation">
                    <List>
                        {navLinks.map(({ to, label, Icon }) => (
                            <ListItemButton key={to} selected={isActive(to)} onClick={() => goTo(to)}>
                                <ListItemIcon><Icon /></ListItemIcon>
                                <ListItemText primary={label} />
                            </ListItemButton>
                        ))}
                        <ListItemButton selected={isRepeaterActive} onClick={(e) => setRepeaterAnchor(e.currentTarget)}>
                            <ListItemIcon><SettingsInputAntennaIcon /></ListItemIcon>
                            <ListItemText primary="Repeaters" />
                        </ListItemButton>
                    </List>
                    <Divider />
                    <List>
                        {socialLinks.map(({ href, label, Icon }) => (
                            <ListItemButton key={href} component="a" href={href} target="_blank" rel="noreferrer">
                                <ListItemIcon><Icon /></ListItemIcon>
                                <ListItemText primary={label} />
                            </ListItemButton>
                        ))}
                    </List>
                </Box>
            </Drawer>
        </AppBar>
    );
};

export default NavCont;
